import React, { useMemo, useState } from "react";
import Main from "../pages/Main";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import StyleIcon from "@mui/icons-material/Style";
import { useNotify } from "../context/NotifyContext";
import { exportTable } from "../utils/downloadExcel";
import WalletHistoryBar from "../components/WalletHistoryBar";
import {
  ListingFilters,
  ListingPagination,
  SortableTh,
  CopyText,
  AppBreadcrumbs,
  useListingState,
  TXN_STATUS_OPTIONS,
  StatusBadge,
} from "../components/listing";

const PaymentHistory = () => {
  const { notify } = useNotify();
  const listing = useListingState({ sortBy: "date", sortDir: "desc" });
  const [tableData, setTableData] = useState([]);
  const dummyData = [
    {
      sno: 1,
      dateTime: "27-11-2025 10:00",
      txnType: "Credit",
      amount: "1000",
      withCharge: "10",
      customerName: "Tanisha",
      consumerNumber: "12345",
      bankRef: "BR123",
      clientRef: "CR123",
      bankName: "SBI",
      status: "SUCCESS",
      platform: "Web",
      merchant: "ABC",
    },
  ];

  const handleLoadRecords = () => {
    setTableData([]);
  };
  const handleAll = () => {
    setTableData(dummyData);
  };
  const handleCreditCard = () => {
    setTableData(dummyData.filter((item) => item.txnType === "Credit"));
  };
  const handleFastag = () => {
    setTableData(dummyData.filter((item) => item.txnType === "Fastag"));
  };

  const filteredRows = useMemo(() => {
    const q = (listing.debouncedSearch || "").toLowerCase();
    let rows = tableData;
    if (listing.status) {
      rows = rows.filter(
        (item) =>
          String(item.status || "").toUpperCase() ===
          String(listing.status).toUpperCase()
      );
    }
    if (!q) return rows;
    return rows.filter((item) =>
      Object.values(item).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [tableData, listing.debouncedSearch, listing.status]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / listing.limit) || 1);
  const pageRows = filteredRows.slice(
    (listing.page - 1) * listing.limit,
    listing.page * listing.limit
  );

  return (
    <Main showRightPanel={false}>
      <div className="mt-4">
        <AppBreadcrumbs
          items={[
            { label: "Dashboard", to: "/" },
            { label: "Market Place" },
            { label: "Utility Payment" },
            { label: "Payment History" },
          ]}
        />

        <WalletHistoryBar title="Utility Payment History" to="/main-wallet-ledger">
          <div className="flex flex-wrap gap-2 items-center justify-between w-full">
            <h2 className="font-semibold text-sm md:text-[15px] text-[var(--text-dark)] m-0">
              Report Type:
            </h2>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={handleAll} className="ds-action-btn">
                <DashboardOutlinedIcon fontSize="small" />
                <span className="hidden sm:inline">All</span>
              </button>
              <button type="button" onClick={handleCreditCard} className="ds-action-btn">
                <CreditCardIcon fontSize="small" />
                <span className="hidden sm:inline">Credit Card</span>
              </button>
              <button type="button" onClick={handleFastag} className="ds-action-btn">
                <StyleIcon fontSize="small" />
                <span className="hidden sm:inline">Fastag</span>
              </button>
            </div>
          </div>
        </WalletHistoryBar>

        <div className="ds-card text-sm">
          <ListingFilters
            onClear={listing.clearFilters}
            startDate={listing.startDate}
            endDate={listing.endDate}
            onStartDate={listing.setStartDate}
            onEndDate={listing.setEndDate}
            search={listing.search}
            onSearch={listing.setSearch}
            searchPlaceholder="Search"
            status={listing.status}
            onStatus={listing.setStatus}
            statusOptions={TXN_STATUS_OPTIONS}
            showApply
            onApply={handleLoadRecords}
            applyLabel="Load Records"
            onExport={() =>
              exportTable(
                "utility-payment-history",
                [
                  { label: "SNO.", key: "sno" },
                  { label: "DATE TIME", key: "dateTime" },
                  { label: "TXN TYPE", key: "txnType" },
                  { label: "AMOUNT", key: "amount" },
                  { label: "WITH CHARGE", key: "withCharge" },
                  { label: "CUSTOMER NAME", key: "customerName" },
                  { label: "CONSUMER NUMBER", key: "consumerNumber" },
                  { label: "BANK REF NUM", key: "bankRef" },
                  { label: "CLIENT REF NUM", key: "clientRef" },
                  { label: "BANK NAME", key: "bankName" },
                  { label: "STATUS", key: "status" },
                  { label: "PLATFORM", key: "platform" },
                  { label: "MERCHANT", key: "merchant" },
                ],
                filteredRows,
                notify
              )
            }
          />

          <div className="ds-table-wrap ds-scroll">
            <table className="ds-table ds-table-listing min-w-[900px]">
              <thead>
                <tr>
                  <th>SNO.</th>
                  <SortableTh
                    label="DATE TIME"
                    sortKey="date"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <th>TXN TYPE</th>
                  <SortableTh
                    label="AMOUNT"
                    sortKey="amount"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <th>WITH CHARGE</th>
                  <th>CUSTOMER NAME</th>
                  <th>CONSUMER NUMBER</th>
                  <th>BANK REF NUM</th>
                  <th>CLIENT REF NUM</th>
                  <th>BANK NAME / ERROR MSG</th>
                  <SortableTh
                    label="STATUS"
                    sortKey="status"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <th>PLATFORM</th>
                  <th>MERCHANT</th>
                  <th>OPERATIONS</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan="14" className="text-center text-[var(--text-muted)]">
                      NO DATA AVAILABLE IN TABLE
                    </td>
                  </tr>
                ) : (
                  pageRows.map((item) => (
                    <tr key={item.sno}>
                      <td>{item.sno}</td>
                      <td>{item.dateTime}</td>
                      <td>{item.txnType}</td>
                      <td>{item.amount}</td>
                      <td>{item.withCharge}</td>
                      <td>{item.customerName}</td>
                      <td>{item.consumerNumber}</td>
                      <td>
                        <CopyText value={item.bankRef} label="Bank Ref" />
                      </td>
                      <td>
                        <CopyText value={item.clientRef} label="Client Ref" />
                      </td>
                      <td>{item.bankName}</td>
                      <td>
                        <StatusBadge status={item.status} />
                      </td>
                      <td>{item.platform}</td>
                      <td>{item.merchant}</td>
                      <td>
                        <button
                          type="button"
                          className="make-text-primary underline"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="font-semibold text-[var(--text-dark)]">
                  <td className="text-center">TOTAL TRANSFER</td>
                  <td className="text-center">
                    {filteredRows
                      .reduce((sum, item) => sum + Number(item.amount), 0)
                      .toFixed(2)}
                  </td>
                  <td className="text-center">
                    {filteredRows
                      .reduce((sum, item) => sum + Number(item.withCharge), 0)
                      .toFixed(2)}
                  </td>
                  <td colSpan="11"></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <ListingPagination
            page={listing.page}
            limit={listing.limit}
            total={filteredRows.length}
            totalPages={totalPages}
            onPageChange={listing.setPage}
            onLimit={listing.setLimit}
            limitOptions={[50, 100, 500]}
          />
        </div>
      </div>
    </Main>
  );
};

export default PaymentHistory;
